import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import msalInstance from "./msalInstance";
import { deleteAuthTokens } from "./localStorage";

const getTokenRequest = () => ({
  scopes: [process.env.REACT_APP_MSAL_SCOPE],
  account: msalInstance.getAllAccounts()[0],
});

// Prevent multiple expired session redirects
let isHandlingExpiredSession = false;

// Cached so unload-time keepalive requests can attach auth synchronously.
let lastAcquiredToken = null;
export const getLastAcquiredToken = () => lastAcquiredToken;

const handleExpiredSession = () => {
  if (isHandlingExpiredSession) return;
  isHandlingExpiredSession = true;

  deleteAuthTokens();
  msalInstance.logoutRedirect({
    account: msalInstance.getAllAccounts()[0],
    onRedirectNavigate: () => {
      window.location.href = process.env.REACT_APP_FE_URL; // Navigate back to login
      return false;  // Don't use Microsofts log out page
    }
  });
};

let apiVersion = process.env.REACT_APP_API_BASE_URL;
const instance = axios.create({
    baseURL: apiVersion,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json', 'charset': 'utf-8' }
})
instance.interceptors.request.use(
    async (config) => {
        try {
            const token = await msalInstance.acquireTokenSilent(getTokenRequest());
            config.headers.pageURL = window.location.href;
            if (token.accessToken) {
                config.headers.Authorization = `Bearer ${token.accessToken}`;
                lastAcquiredToken = token.accessToken;
            }
        } catch (e) {
            if (e instanceof InteractionRequiredAuthError) {
                handleExpiredSession();
            }
            return Promise.reject(e);
        }
        return config;
    },
    (error) => Promise.reject(error)
);

let isAlreadyFetchingAccessToken = false;
let requests = [];

const onAccessTokenFetched = (access_token) => {
    requests = requests.filter((callback) => callback(access_token));
};

const flushRequestsWithError = (error) => {
    requests.forEach((callback) => callback(null, error));
    requests = [];
};

const addRequest = (callback) => {
    requests.push(callback);
};

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;
    if (status === 401) {
      if (!isAlreadyFetchingAccessToken) {
        isAlreadyFetchingAccessToken = true;
        msalInstance.acquireTokenSilent(getTokenRequest())
          .then((tokenResponse) => {
            isAlreadyFetchingAccessToken = false;
            onAccessTokenFetched(tokenResponse.accessToken);
          })
          .catch((err) => {
            isAlreadyFetchingAccessToken = false;
            flushRequestsWithError(err);
            if (err instanceof InteractionRequiredAuthError) {
              handleExpiredSession();
            }
          });
      }
      const retryOriginalRequest = new Promise((resolve, reject) => {
        addRequest((access_token, error) => {
          if (error) return reject(error);
          originalRequest.headers.Authorization = "Bearer " + access_token;
          resolve(instance.request(originalRequest));
        });
      });
      return retryOriginalRequest;
    }
    return Promise.reject(error);
  }
);

export default instance;
